import React from "react";
import { ArrowRight, Clock, Route } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { buildWhatsAppLink, ROUTES_STATIC } from "../data/static";
import { formatPrice } from "../utils/currency";

export const OneWayTaxi = () => {
  const { t } = useI18n();
  const o = t.oneway;

  return (
    <section id="oneway" data-testid="oneway-section" className="relative py-20 lg:py-28 bg-white border-y border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D]">{o.eyebrow}</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
              {o.title} <span className="italic text-slate-500">{o.titleItalic}</span>
            </h2>
            <p className="mt-4 text-base text-slate-600">{o.subtitle}</p>
          </div>
          <div className="text-sm text-slate-500">{o.note}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {o.routes.map((r, idx) => {
            const stat = ROUTES_STATIC[r.id];
            const link = buildWhatsAppLink(o.waMsg(r.from, r.to));
            return (
              <article key={r.id} data-testid={`oneway-card-${r.id}`}
                className="group relative bg-[#FAFAF7] rounded-3xl overflow-hidden border border-slate-200/80 hover:border-[#D1603D]/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${idx * 80}ms` }}>
                <div className="relative h-44 overflow-hidden">
                  <img src={stat.image} alt={`${r.from} to ${r.to}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/65 via-slate-900/10 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 text-white">
                    <span className="font-display text-2xl font-semibold">{r.from}</span>
                    <span className="flex-1 h-px bg-white/40 relative">
                      <ArrowRight className="w-4 h-4 absolute -right-1 -top-2 text-white" />
                    </span>
                    <span className="font-display text-2xl font-semibold">{r.to}</span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-5 text-sm text-slate-600">
                    <div className="flex items-center gap-1.5">
                      <Route className="w-4 h-4 text-[#D1603D]" />
                      {stat.distanceKm} {o.km}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-[#D1603D]" />
                      {stat.durationHrs}
                    </div>
                  </div>

                  <ul className="mt-4 space-y-1.5">
                    {r.highlights.map((h) => (
                      <li key={h} className="text-xs text-slate-500 flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-[#D1603D]" />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex items-end justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">{o.startsFrom}</div>
                      <div className="font-display text-2xl font-semibold text-slate-900">
                        {formatPrice(stat.startingPrice)}
                      </div>
                    </div>
                    <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`oneway-book-${r.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D1603D] text-white text-sm font-semibold hover:bg-[#B34A2B] transition-colors active:scale-95">
                      {o.bookNow}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OneWayTaxi;
